import React from "react";
import * as Icons from "lucide-react";
import { RagBadge } from "@/components/common/RagBadge";
import { LinkChip } from "@/components/workspace/ActionLinkPicker";
import { fmtDate, ACTION_STATUS, relDays } from "@/lib/format";

export function WorkItemRow({ a, onOpen, showOwner = false }) {
  const st = ACTION_STATUS[a.status] || ACTION_STATUS.not_started;
  const days = relDays(a.due_date);
  const closed = ["completed", "cancelled"].includes(a.status);
  return (
    <button
      type="button"
      onClick={() => onOpen(a)}
      className="amden-card amden-card-hover flex w-full items-start gap-3 p-4 text-left"
      data-testid={`work-item-${a.id}`}
    >
      <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${a.status === "blocked" ? "bg-[#C95555]" : closed ? "bg-[#3D8B65]" : a.status === "awaiting_review" ? "bg-[#D99A32]" : "bg-[#4A78A8]"}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-sm font-medium ${closed ? "text-[#1E2D44]/55 line-through decoration-[#1E2D44]/25" : "text-[#0B1729]"}`}>{a.title}</span>
          {a.cadence && a.cadence !== "one_off" && (
            <span className="inline-flex items-center gap-1 rounded-md bg-[#4A78A8]/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#4A78A8]">
              <Icons.Repeat size={10} /> {a.cadence}
            </span>
          )}
          {a.requires_review && !closed && <Icons.ShieldCheck size={13} className="text-[#B87F1E]" title="Needs independent review" />}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <LinkChip a={a} />
          {showOwner && <span className="text-xs text-[#1E2D44]/55">{a.owner_name}</span>}
          {(a.evidence || []).length > 0 && (
            <span className="flex items-center gap-1 text-xs text-[#1E2D44]/50"><Icons.Paperclip size={11} /> {a.evidence.length}</span>
          )}
        </div>
        {a.status === "blocked" && a.blocker && (
          <div className="mt-2 flex items-start gap-1.5 text-xs text-[#C95555]"><Icons.OctagonAlert size={13} className="mt-px shrink-0" /> {a.blocker}</div>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <RagBadge rag={st.rag}>{st.label}</RagBadge>
        <span className={`text-xs ${!closed && days != null && days < 0 ? "font-semibold text-[#C95555]" : "text-[#1E2D44]/50"}`}>
          {a.due_date ? (!closed && days < 0 ? `Overdue · ${fmtDate(a.due_date)}` : fmtDate(a.due_date)) : "No due date"}
        </span>
      </div>
    </button>
  );
}

export function WorkGroup({ title, items, onOpen, showOwner, testId, empty }) {
  if (!items.length && !empty) return null;
  return (
    <section className="mb-7" data-testid={testId}>
      <div className="mb-2.5 flex items-center gap-2">
        <h3 className="font-display text-sm font-semibold text-[#0B1729]">{title}</h3>
        <span className="rounded-full bg-[#E4E8EF] px-2 py-0.5 text-[11px] font-medium text-[#1E2D44]/60">{items.length}</span>
      </div>
      {items.length ? (
        <div className="space-y-2">{items.map((a) => <WorkItemRow key={a.id} a={a} onOpen={onOpen} showOwner={showOwner} />)}</div>
      ) : (
        <p className="rounded-xl border border-dashed border-[#D2D9E3] px-4 py-5 text-center text-sm text-[#1E2D44]/45">{empty}</p>
      )}
    </section>
  );
}
